import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';
import Navigation from '../Navigation/navigation';

const EditJobInfo = () => {
  const { staffId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [staffData, setStaffData] = useState(null);
  const [jobInfo, setJobInfo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [referenceData, setReferenceData] = useState({
    clinics: [],
    townships: [],
    districts: [],
    cadres: [],
    jobPositions: []
  });

  const isOtherPosition = (positionId) => {
    const selectedPosition = referenceData.jobPositions.find(
      (position) => String(position.id) === String(positionId)
    );
    return selectedPosition?.position_name?.trim() === 'Other';
  };

  const isOtherCadre = (cadreId) => {
    const selectedCadre = referenceData.cadres.find(
      (cadre) => String(cadre.id) === String(cadreId)
    );
    return selectedCadre?.name?.trim().toLowerCase() === 'other';
  };

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    if (staffId) {
      fetchStaffData();
      fetchJobInfo();
      fetchReferenceData();
    }
  }, [staffId, isAuthenticated, user, navigate]);

  const fetchStaffData = async () => {
    try {
      const response = await api.get(`/api/staff/${staffId}`);
      setStaffData(response.data);
    } catch (error) {
      console.error('Error fetching staff data:', error);
      setError('Error retrieving staff information.');
    }
  };

  const fetchJobInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/staff/${staffId}/job-info`);
      const jobDataArray = response.data || [];
      
      // Check if we have job data
      if (Array.isArray(jobDataArray) && jobDataArray.length > 0) {
        // Use existing job data - convert dates to proper format for input fields
        const formattedJobData = jobDataArray.map(job => ({
          id: job.id,
          duty_location: job.duty_location || '',
          township_id: job.township_id || '',
          district_id: job.district_id || '',
          appointed_by_bma: Boolean(job.appointed_by_bma),
          position_id: job.position_id || '',
          other_workposition: job.other_workposition || '',
          current_cadre: job.current_cadre || '',
          other_cadre: job.other_cadre || '',
          start_working_date: job.start_working_date ? job.start_working_date.split('T')[0] : '',
          end_working_date: job.end_working_date ? job.end_working_date.split('T')[0] : '',
          currently_working: !job.end_working_date || job.end_working_date === ''
        }));
        setJobInfo(formattedJobData);
      } else {
        // Initialize with one empty job record if no data exists
        setJobInfo([{
          duty_location: '',
          township_id: '',
          district_id: '',
          appointed_by_bma: false,
          position_id: '',
          other_workposition: '',
          current_cadre: '',
          other_cadre: '',
          start_working_date: '',
          end_working_date: '',
          currently_working: false
        }]);
      }
    } catch (error) {
      console.error('Error fetching job info:', error);
      setError('Error retrieving job information.');
      // Initialize with empty job on error
      setJobInfo([{
        duty_location: '',
        township_id: '',
        district_id: '',
        appointed_by_bma: false,
        position_id: '',
        other_workposition: '',
        current_cadre: '',
        other_cadre: '',
        start_working_date: '',
        end_working_date: '',
        currently_working: false
      }]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [clinics, townships, districts, cadres, jobPositions] = await Promise.all([
        api.get('/api/reference/clinics'),
        api.get('/api/reference/townships'),
        api.get('/api/reference/districts'),
        api.get('/api/reference/cadres'),
        api.get('/api/reference/jobpositions')
      ]);
      
      // Helper function to extract data from response (handles both direct arrays and wrapped responses)
      const extractData = (response) => {
        if (Array.isArray(response.data)) {
          return response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          return response.data.data;
        } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
          return response.data.data;
        }
        return [];
      };
      
      setReferenceData({
        clinics: extractData(clinics),
        townships: extractData(townships),
        districts: extractData(districts),
        cadres: extractData(cadres),
        jobPositions: extractData(jobPositions)
      });
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const addJobInfo = () => {
    const newJob = {
      duty_location: '',
      township_id: '',
      district_id: '',
      appointed_by_bma: false,
      position_id: '',
      other_workposition: '',
      current_cadre: '',
      other_cadre: '',
      start_working_date: '',
      end_working_date: '',
      currently_working: false
    };
    setJobInfo(prev => [...prev, newJob]);
  };

  const updateJobInfo = (index, field, value) => {
    setJobInfo(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Auto-populate township based on clinic selection
      if (field === 'duty_location' && value) {
        const selectedClinic = referenceData.clinics.find(clinic => clinic.clinic_id === value);
        if (selectedClinic) {
          updated[index]['township_id'] = selectedClinic.township_id || '';
        }
      }
      
      return updated;
    });
  };

  const removeJobInfo = (index) => {
    if (jobInfo.length > 1) {
      setJobInfo(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Clean up job info: if currently_working is true, ensure end_working_date is empty
      const cleanedJobInfo = jobInfo.map(job => ({
        ...job,
        end_working_date: job.currently_working ? '' : job.end_working_date
      }));
      
      // Send jobInfo as array (like work experience)
      await api.put(`/api/staff/${staffId}/job-info`, { jobInfo: cleanedJobInfo });
      setSuccessMessage('Job information updated successfully!');
      setTimeout(() => {
        navigate(`/edit-staff/${staffId}`);
      }, 2000);
    } catch (error) {
      console.error('Error updating job info:', error);
      setError(error.response?.data?.message || 'Error updating job information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/edit-staff/${staffId}`);
  };

  if (loading && !staffData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-6xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h4 className="text-xl font-semibold text-gray-800">Loading Job Information...</h4>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto p-5 pt-20">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Edit Job Information</h1>
              <p className="text-gray-600">
                Managing job details for: <strong>{staffData?.fullname}</strong> (ID: {staffData?.staff_id})
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Success and Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Job Information</h2>
                <p className="text-blue-100 mt-1">Manage work positions, locations, and employment details</p>
              </div>
              <button
                type="button"
                onClick={addJobInfo}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
              >
                Add Job Information
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {jobInfo.length === 0 && (
              <div className="text-center py-8 text-gray-600 mb-6">
                <div className="text-4xl mb-4">💼</div>
                <p className="text-lg font-medium">No job information yet</p>
                <p className="text-sm text-gray-600 mt-2">Click "Add Job Information" to get started</p>
              </div>
            )}

            {jobInfo.map((job, index) => (
              <div key={index} className="job-item border border-gray-200 rounded-lg p-6">
                <div className="job-header flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-800">Job Position #{index + 1}</h3>
                  {jobInfo.length > 1 && (
                    <button
                      type="button"
                      className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 transition-colors"
                      onClick={() => removeJobInfo(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Duty Location <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={job.duty_location || ''}
                      onChange={(e) => updateJobInfo(index, 'duty_location', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      required
                    >
                      <option value="">Select Clinic</option>
                      {referenceData.clinics.map((clinic) => (
                        <option key={clinic.clinic_id} value={clinic.clinic_id}>
                          {clinic.clinic_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Township <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={job.township_id || ''}
                      onChange={(e) => updateJobInfo(index, 'township_id', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      required
                    >
                      <option value="">Select Township</option>
                      {referenceData.townships.map((township) => (
                        <option key={township.township_id} value={township.township_id}>
                          {township.township_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      District <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={job.district_id || ''}
                      onChange={(e) => updateJobInfo(index, 'district_id', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      required
                    >
                      <option value="">Select District</option>
                      {referenceData.districts.map((district) => (
                        <option key={district.district_id} value={district.district_id}>
                          {district.district_name} - {district.district_desc}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={job.appointed_by_bma || false}
                        onChange={(e) => updateJobInfo(index, 'appointed_by_bma', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span>Supported by BMA</span>
                    </label>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Work Position <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={job.position_id || ''}
                      onChange={(e) => updateJobInfo(index, 'position_id', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      required
                    >
                      <option value="">Select Work Position</option>
                      {referenceData.jobPositions.map(position => (
                        <option key={position.id} value={position.id}>{position.position_name}</option>
                      ))}
                    </select>
                  </div>

                  {isOtherPosition(job.position_id) && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Other Work Position <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={job.other_workposition || ''}
                        onChange={(e) => updateJobInfo(index, 'other_workposition', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                        placeholder="Specify other work position"
                        required
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Cadre <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={job.current_cadre || ''}
                      onChange={(e) => updateJobInfo(index, 'current_cadre', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      required
                    >
                      <option value="">Select Cadre</option>
                      {referenceData.cadres.map((cadre) => (
                        <option key={cadre.id} value={cadre.id}>
                          {cadre.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {isOtherCadre(job.current_cadre) && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Other Cadre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={job.other_cadre || ''}
                        onChange={(e) => updateJobInfo(index, 'other_cadre', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                        placeholder="Specify other cadre"
                        required
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formatDateForInput(job.start_working_date)}
                      onChange={(e) => updateJobInfo(index, 'start_working_date', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="date"
                      value={formatDateForInput(job.end_working_date)}
                      onChange={(e) => updateJobInfo(index, 'end_working_date', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      disabled={job.currently_working}
                    />
                    <label className="flex items-center space-x-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={job.currently_working || false}
                        onChange={(e) => {
                          updateJobInfo(index, 'currently_working', e.target.checked);
                          if (e.target.checked) {
                            updateJobInfo(index, 'end_working_date', '');
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 font-medium">✓ Currently working here</span>
                    </label>
                  </div>
                </div>

                {/* Job Summary */}
                {job.duty_location && job.position_id && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                    <p className="text-sm text-gray-800">
                      <strong>Summary:</strong> {referenceData.jobPositions.find((p) => String(p.id) === String(job.position_id))?.position_name} at {referenceData.clinics.find(c => c.clinic_id === job.duty_location)?.clinic_name}
                      {job.start_working_date && ` (Started: ${new Date(job.start_working_date).toLocaleDateString()})`}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditJobInfo;
